import { createElement } from 'react';

import { useHasPermission } from '../../hooks';

interface WithPermissionProps<TElementType extends React.ElementType> {
  requiredPermission: string | string[];
  children?: React.ReactNode;
  options?: {
    operator?: 'AND' | 'OR';
  };
  errorElement?: TElementType | null;
  slotProps?: {
    errorElement?: React.ComponentProps<TElementType>;
  };
}

/**
 * A higher-order component that conditionally renders its children based on permission requirements.
 * @param {WithPermissionProps<TElementType>} props - Configuration for permission-based rendering.
 * @returns Either the children or an error element based on permission status.
 *
 * @example
 * ```tsx
 * <WithPermission requiredPermission={APP_PERMISSIONS.SETTING.USER.VIEW}>
 *  <button>Click me</button>
 * </WithPermission>
 * ```
 *
 * // if user has permission, render
 *
 * ```tsx
 * <button>Click me</button>
 * ```
 *
 * // if user does not have permission, it renders nothing
 *
 * @example
 * ```tsx
 * <WithPermission
 *  requiredPermission={APP_PERMISSIONS.SETTING.USER.VIEW}
 *  errorElement="p"
 *  slotProps={{
 *    errorElement: {
 *      children: 'You do not have permission to view this page',
 *    },
 *  }}
 * >
 *  <button>Click me</button>
 * </WithPermission>
 *
 * // if user has permission, render
 * ```tsx
 * <button>Click me</button>
 * ```
 *
 * // if user does not have permission, it renders
 * ```tsx
 * <p>You do not have permission to view this page</p>
 * ```
 */
export default function WithPermission<TElementType extends React.ElementType>(
  props: Readonly<WithPermissionProps<TElementType>>,
) {
  const {
    requiredPermission,
    children,
    errorElement,
    options = {
      operator: 'AND',
    },
    slotProps,
  } = props;
  const { errorElement: errorElementProps } = slotProps ?? {};

  const hasPermission = useHasPermission(requiredPermission, options?.operator);

  if (hasPermission) {
    return children;
  }

  if (errorElement) {
    return createElement(errorElement, errorElementProps);
  }

  return null;
}
